// Cross-section fan-out banner (session 96; s97 polish). Surfaces, below the upload
// section's own extraction review, that the uploaded document also produced data for
// OTHER sections. Additive and dismissible. Two states (s97 Fix 1):
//   - prompting: "…also contains information for N sections" + Review & Populate / Skip
//   - success:   "Data added to N sections ✓" + Dismiss (after every section populated)
// Both states carry an X close button. Presentational: the host (GenericCategoryPage)
// owns the data, the populated count, and the Review modal it opens.

import { Check, Sparkles, X } from "lucide-react";

export interface FanOutSection {
  sectionId: string;
  sectionSlug: string;
  sectionName: string;
  rows: Array<{ fields: Array<{ fieldId: string; fieldKey: string; name: string; value: string }> }>;
  /** DB-45 1I-B: how many records this section ALREADY has (server-computed). Shown in
   *  the review modal as a muted "N records already" heads-up — informational only. */
  existingRecordCount?: number;
}

interface FanOutBannerProps {
  sections: FanOutSection[];
  /** Sections populated so far this session — drives the success state. */
  populatedCount: number;
  /** True once every fan-out section has been populated. */
  allPopulated: boolean;
  onReview: () => void;
  onSkip: () => void;
  /** X / Dismiss — fully hides the banner. */
  onClose: () => void;
}

export function FanOutBanner({
  sections,
  populatedCount,
  allPopulated,
  onReview,
  onSkip,
  onClose,
}: FanOutBannerProps) {
  if (sections.length === 0) return null;

  // ── Success state: everything populated → confirmation, Dismiss only. ──
  if (allPopulated) {
    return (
      <div className="px-4 py-3 rounded-lg border border-sage-200 bg-sage-50 text-sm flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 flex-1">
          <Check className="h-4 w-4 text-sage-700 mt-0.5 shrink-0" />
          <p className="text-sage-900">
            Data added to{" "}
            <span className="font-semibold">
              {populatedCount} section{populatedCount !== 1 ? "s" : ""}
            </span>{" "}
            <Check className="h-4 w-4 inline-block align-[-2px]" aria-hidden="true" />
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <button
            className="text-xs font-medium text-sage-800 hover:text-sage-900 underline"
            onClick={onClose}
            data-testid="button-fanout-dismiss"
          >
            Dismiss
          </button>
          <button
            aria-label="Close"
            className="text-muted-foreground hover:text-foreground"
            onClick={onClose}
            data-testid="button-fanout-close-x"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  // ── Prompting state. ──
  const names = sections.map((s) => s.sectionName).join(", ");
  return (
    <div className="px-4 py-3 rounded-lg border border-sage-200 bg-sage-50 text-sm flex items-start justify-between gap-3">
      <div className="flex items-start gap-2 flex-1">
        <Sparkles className="h-4 w-4 text-sage-700 mt-0.5 shrink-0" />
        <p className="text-sage-900">
          This document also contains information for{" "}
          <span className="font-semibold">
            {sections.length} other section{sections.length !== 1 ? "s" : ""}
          </span>
          : {names}.
        </p>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <button
          className="text-xs text-muted-foreground hover:text-foreground underline"
          onClick={onSkip}
          data-testid="button-fanout-skip"
        >
          Skip
        </button>
        <button
          className="text-xs font-medium text-sage-800 hover:text-sage-900 underline"
          onClick={onReview}
          data-testid="button-fanout-review"
        >
          Review &amp; Populate
        </button>
        <button
          aria-label="Close"
          className="text-muted-foreground hover:text-foreground"
          onClick={onClose}
          data-testid="button-fanout-close-x"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
